import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../shell/bridge';
import { MIN_QUERY_LENGTH, rankRows, type FuzzyField } from '../../../../shared/fuzzy';
import { describeResult, itemLabel as labelFor } from '../../lib/searchLabel';
import './search.css';

/* ---------------------------------------------------------------------------
   Search — card UW7Lu06zMJp9 (epic BpnqEtUIYiSi).

   Jonathan 2026-10-25 made this a destination of its own rather than a bar
   pinned inside Notebook, and asked that a tag someone created "come up in a
   rich element" instead of reading as text in a result line. Both land here:

     - one screen, opened from the More drawer, closed with Close or Escape;
     - every result carries its collection and its OWN tags as chips — each
       chip is a tap target that re-runs the search for that tag, which is the
       only reason a chip deserves to be more than a word.

   Matching is shared/fuzzy.ts, not a LIKE: a word typed wrong still finds its
   card, and a tag that appears inside a longer tag still matches.

   The corpora are the two reads Notebook already makes (the shared list and
   the tag scan), so search needs no new endpoint and no schema — the card's
   approach line ("client-side fuzzy pass first") taken literally.
   --------------------------------------------------------------------------- */

type TagKind = 'mood' | 'theme' | 'time';
const TAG_KIND_LABEL: Record<TagKind, string> = { mood: 'Mood', theme: 'Theme', time: 'Time' };

/** Only the fields search reads. Narrow on purpose: the screen must not grow a
 *  dependency on the Notebook screen's own row type (a second copy of it would
 *  drift the first time a field moved, which is card 4CXp7HfSIAGd's lesson). */
interface SearchItem {
  itemId: string;
  url: string;
  title: string | null;
  description: string | null;
  body: string | null;
  provider: string;
  entryType: 'link' | 'gift' | 'memory' | 'date' | 'note' | 'idea';
  kind: string;
  category: string;
  doneAt: number | null;
}

interface SearchTag {
  id: string;
  itemId: string;
  kind: TagKind;
  tag: string;
}

/** The collection a result belongs to, in the app's own words. */
const ENTRY_LABEL: Record<SearchItem['entryType'], string> = {
  link: 'Collection',
  gift: 'Gifts',
  memory: 'Memories',
  date: 'Date ideas',
  note: 'Notes',
  idea: 'Ideas',
};

interface SearchProps {
  onClose: () => void;
  /** Open a result (card 5wr2rXdlCSTh): a row used to be inert text, so a
   *  person could find the card and still had to go and hunt for it. The
   *  screen does not navigate itself — the shell owns routing — so it hands
   *  the row over and lets the caller land on the item. */
  onOpen: (item: SearchItem) => void;
}

/* Card r9ClOD0okEnZ: the label chain lives in web/src/lib/searchLabel.ts —
   a Memory has no title and no page URL, so this screen used to print
   'Untitled' for a card whose words it had just matched. */
const itemLabel = labelFor;

export function Search({ onClose, onOpen }: SearchProps) {
  const [items, setItems] = useState<SearchItem[] | null>(null);
  const [tags, setTags] = useState<SearchTag[]>([]);
  /* The panel unmounts every time you leave Search, so the query is kept for
     the session: arriving Back on this screen has to show the results you
     left, not an empty box (card 5wr2rXdlCSTh's landing walk). Private-mode
     storage that throws is not a failure — the box just starts empty. */
  const QUERY_KEY = 'pear.search.query';
  const readQuery = () => {
    try { return sessionStorage.getItem(QUERY_KEY) ?? ''; } catch { return ''; }
  };
  const [query, setQueryRaw] = useState(readQuery);
  const setQuery = useCallback((q: string) => {
    setQueryRaw(q);
    try {
      if (q) sessionStorage.setItem(QUERY_KEY, q);
      else sessionStorage.removeItem(QUERY_KEY);
    } catch { /* no session storage — the box still works */ }
  }, []);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [list, tagOut] = await Promise.all([
          api<{ items: SearchItem[] }>('/api/notebook'),
          api<{ tags: SearchTag[] }>('/api/notebook/tags').catch(() => ({ tags: [] })),
        ]);
        if (!live) return;
        setItems(list.items ?? []);
        setTags(tagOut.tags ?? []);
      } catch {
        if (!live) return;
        setError('Search could not reach your notebook. Close and try again.');
        setItems([]);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  /* Focus lands in the field on arrival — this screen has exactly one job. */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /* Escape closes the panel, the same key the More drawer honours. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  /** Tags grouped by the item they were created on. */
  const tagsByItem = useMemo(() => {
    const map = new Map<string, SearchTag[]>();
    for (const t of tags) {
      const list = map.get(t.itemId);
      if (list) list.push(t);
      else map.set(t.itemId, [t]);
    }
    return map;
  }, [tags]);

  /* Weights: a title hit is the strongest signal, then the tag a person
     created, then the description, then the saved body. The tag KIND is
     searchable too ("theme") because the card asks for tag type, not only the
     tag's value. */
  const fieldsOf = useCallback(
    (item: SearchItem): FuzzyField[] => {
      const own = tagsByItem.get(item.itemId) ?? [];
      return [
        { key: 'title', text: item.title, weight: 1.15 },
        { key: 'tag', text: own.map(t => t.tag).join(' '), weight: 1.1 },
        { key: 'tagKind', text: own.map(t => `${t.kind} ${TAG_KIND_LABEL[t.kind]}`).join(' '), weight: 1.0 },
        { key: 'description', text: item.description, weight: 0.95 },
        { key: 'body', text: item.body, weight: 0.7 },
        { key: 'category', text: `${item.entryType} ${item.kind} ${item.category}`, weight: 0.6 },
      ];
    },
    [tagsByItem],
  );

  const results = useMemo(() => {
    if (!items || query.trim().length < MIN_QUERY_LENGTH) return [];
    return rankRows(query, items, fieldsOf, itemLabel).slice(0, 40);
  }, [items, query, fieldsOf]);

  const searching = query.trim().length >= MIN_QUERY_LENGTH;
  const loading = items === null;

  return (
    <div className="search" data-search-screen>
      <div className="search-head">
        <h2 className="search-title">Search</h2>
        <button type="button" className="search-close" id="btn-search-close" onClick={onClose}>
          Close
        </button>
      </div>

      <label className="search-field">
        <span className="search-field-label">Find a card, a word or a tag</span>
        <input
          ref={inputRef}
          id="search-input"
          className="search-input"
          type="search"
          inputMode="search"
          autoComplete="off"
          value={query}
          placeholder="Try a word from the description, or a tag"
          onChange={e => setQuery(e.target.value)}
          aria-describedby="search-status"
        />
      </label>

      <p className="search-status" id="search-status" role="status" aria-live="polite">
        {error
          ? error
          : loading
            ? 'Reading your notebook…'
            : !searching
              ? 'Search matches any word — descriptions and the tags you created. Spelling does not have to be exact.'
              : `${results.length}${results.length === 40 ? '+' : ''} ${results.length === 1 ? 'card' : 'cards'} for “${query.trim()}”`}
      </p>

      {searching && !loading && results.length === 0 && !error && (
        <p className="search-empty">Nothing matched. Try one word, or a tag’s first few letters.</p>
      )}

      <ul className="search-results">
        {results.map(({ row: item, matched }) => {
          const own = tagsByItem.get(item.itemId) ?? [];
          /* Card r9ClOD0okEnZ: a typed card (Memory, Note, Idea, Gift) carries
             its words in `body`, not in a description — so the row now returns
             the card's own text and the collection chip names what it is. */
          const words = describeResult(item);
          return (
            <li
              key={item.itemId}
              className="search-result"
              data-item-id={item.itemId}
              data-entry-type={item.entryType}
              role="button"
              tabIndex={0}
              aria-label={`Open ${itemLabel(item)} in ${ENTRY_LABEL[item.entryType] ?? 'Notebook'}`}
              onClick={() => onOpen(item)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(item);
                }
              }}
            >
              <div className="search-result-top">
                <span className="search-result-title">{words.label}</span>
                <span className="search-collection" data-entry-type={item.entryType}>
                  {ENTRY_LABEL[item.entryType] ?? 'Notebook'}
                </span>
              </div>
              {words.preview && (
                <p
                  className={`search-result-desc${words.previewField && matched.includes(words.previewField) ? ' is-hit' : ''}`}
                  data-preview-field={words.previewField ?? undefined}
                >
                  {words.preview}
                </p>
              )}
              {own.length > 0 && (
                <div className="search-tags">
                  {own.map(t => (
                    <button
                      type="button"
                      key={t.id}
                      className={`search-tag${matched.includes('tag') || matched.includes('tagKind') ? ' is-hit' : ''}`}
                      data-tag-kind={t.kind}
                      data-tag={t.tag}
                      aria-label={`Search for the ${TAG_KIND_LABEL[t.kind].toLowerCase()} tag ${t.tag}`}
                      onClick={e => {
                        e.stopPropagation();
                        setQuery(t.tag);
                      }}
                    >
                      <span className="search-tag-kind">{TAG_KIND_LABEL[t.kind]}</span>
                      {t.tag}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
